import React, { useState, useEffect, useRef, useMemo } from 'react';
import RoundIcon from 'components/common/RoundIcon';
import ProgressRing from 'components/common/ProgressRing';
import useStore from 'store/timeline';
import useGlobalStore from 'store/global';
import Button from 'components/common/Button';
import styles from './CommentBox.module.scss';
import TextEditor, { TextEditorRef } from 'components/common/TextEditor';
import {
  MediaType,
  MembershipPackageName,
  PostType,
  User,
} from 'common/interfaces/api';
import { ScreenName } from 'common/utils/pp_tracking';
import {
  BlackListUrl,
  checkPolicyCommentText,
  PolicyError,
  PolicyLevel,
} from 'service/post';
import debounce from 'common/utils/debounce';
import { useMutation } from 'react-query';
import { POST_POLICY_URL } from 'common/utils';
import { track } from '@amplitude/analytics-browser';
import { exCreatorPostLockMessage } from 'common/utils/post';

export type CommentBoxProps = {
  sendMessage: (mess: string) => void;
  isAbleToComment: boolean;
  isLocked?: boolean;
  creator?: User;
  postType: PostType;
};
export default function LiveStreamCommentBox({
  sendMessage,
  isAbleToComment,
  isLocked = false,
  creator,
  postType,
}: CommentBoxProps): JSX.Element {
  const charLimit = 300;
  const currentUser = useStore((state) => state.currentUser);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const [postable, setPostable] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [plainText, setPlainText] = useState('');
  const textLength = useMemo(() => plainText.length, [plainText]);
  const textEditorRef = useRef<TextEditorRef>();
  const showPaymentDialog = useGlobalStore((state) => state.showPaymentDialog);
  const setShowMembershipPayDialog = useGlobalStore(
    (state) => state.setShowMembershipPayDialog
  );
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage
  );
  const [textPolicies, setTextPolicies] = useState<PolicyError[]>([]);
  const [blackUrls, setBlackUrls] = useState<BlackListUrl[]>([]);
  const usingTextPolices = useMemo(() => {
    return textPolicies.filter((po) => po.level === PolicyLevel.SuperHigh);
  }, [textPolicies]);
  const hasPolicyError = usingTextPolices.length > 0 || blackUrls.length > 0;

  useEffect(() => {
    setPostable(
      textLength > 0 && textLength <= charLimit && plainText.trim().length > 0
    );
  }, [textLength, charLimit, plainText]);

  const { mutate: checkPostText, isLoading: isCheckingText } = useMutation(
    async (plainText: string) => {
      return checkPolicyCommentText(plainText);
    },
    {
      onSuccess: (data) => {
        // only update when have different data
        const policies = data?.data?.words || [];
        const isSame =
          policies.length === textPolicies.length &&
          policies.every((pl) =>
            textPolicies.some((policy) => policy.name === pl.name)
          );
        if (!isSame) setTextPolicies(policies);
        const urls = data?.data?.urls || [];
        const isSameUrls =
          urls.length === blackUrls.length &&
          urls.every((blUrl) => blackUrls.some((url) => url.url === blUrl.url));
        if (!isSameUrls) setBlackUrls(urls);
      },
    }
  );

  const debouncedCheckPostText = useMemo(
    () => debounce(checkPostText, 1000),
    [checkPostText]
  );

  useEffect(() => {
    if (!textEditorRef.current) return;
    textEditorRef.current.forceRender();
  }, [textPolicies, blackUrls]);

  useEffect(() => {
    debouncedCheckPostText(plainText);
  }, [plainText, debouncedCheckPostText]);

  const handleMakeComment = () => {
    if (!postable || !isAbleToComment || isCheckingText) return;
    if (hasPolicyError) return;
    if (isBlocking) return;
    if (!currentUser) {
      showLogin({});
    } else {
      if (isLocked) {
        if (postType === PostType.Gold) {
          setShowMembershipPayDialog({
            open: true,
            showMembershipStatus: false,
            initPackage: MembershipPackageName.Platinum,
            screen: ScreenName.PostDetail,
          });
          setSnackbarMessage({
            type: 'warning',
            text: 'GOLDメンバーシップに登録してLIVEに参加しましょう',
            autoHideDuration: 10000,
          });
        } else {
          if (creator.is_open_for_subscription) {
            showPaymentDialog({ creator: creator });
            const message =
              postType === PostType.Plus
                ? 'プライムPlusを登録してLIVEに参加しましょう'
                : postType === PostType.Paid
                ? 'プライム登録してLIVEに参加しましょう'
                : '';
            if (message) {
              setSnackbarMessage({
                type: 'warning',
                text: message,
                autoHideDuration: 10000,
              });
            }
          } else {
            setSnackbarMessage({
              type: 'error',
              text: exCreatorPostLockMessage(postType),
            });
          }
        }
        return;
      }
      let messsageContent = '';
      try {
        messsageContent = textEditorRef.current?.getContent();
      } catch (error) {
        messsageContent = '';
      }
      messsageContent = messsageContent.replace(/\n/g, ' ').trim();
      if (messsageContent.length === 0) return;
      sendMessage(messsageContent);
      track('Send Live Chat', {
        post_type: postType,
        media_type: MediaType.Stream,
      });
      textEditorRef.current?.reset();
      setIsBlocking(true);
      setTimeout(() => {
        setIsBlocking(false);
      }, 1000);
    }
  };

  const avatarEl = (
    <div className={styles.avatar}>
      <RoundIcon
        user={currentUser}
        width={35}
        height={35}
        hasRing
        emotion={currentUser?.emotion}
      />
    </div>
  );

  return (
    <>
      <div className={styles.commentBox}>
        {hasPolicyError && (
          <div className={styles.alert}>
            投稿ポリシーに違反している可能性があります。詳しくは
            <a target="_blank" href={POST_POLICY_URL}>
              投稿ポリシー
            </a>
            をご覧ください。
          </div>
        )}
        {avatarEl}
        <div className={styles.inputBox}>
          <div className={styles.textInputWrapper}>
            <div className={styles.textInput}>
              <TextEditor
                ref={textEditorRef}
                setPlainText={setPlainText}
                onEnter={handleMakeComment}
                placeholder="コメントする"
                initialText=""
                charLimit={charLimit}
                blackUrls={blackUrls}
                textPolicies={usingTextPolices}
              />
            </div>
          </div>
          <div className={styles.rightSide}>
            <div className={styles.progress}>
              <ProgressRing
                radius={18}
                current={textLength}
                total={charLimit}
              />
            </div>
            <Button
              disabled={
                !postable ||
                !isAbleToComment ||
                isBlocking ||
                hasPolicyError ||
                isCheckingText
              }
              onClick={handleMakeComment}
              text="送信"
              rounded
              verticalPadding={6}
            />
          </div>
        </div>
      </div>
    </>
  );
}
